import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useStrings } from '@/i18n/Localization';
import { applyMention, mentionQueryAt } from '@/protocol/entities';
import { AttachmentPicker } from '@/components/AttachmentPicker';
import { MentionPicker } from '@/components/MentionPicker';
import { UploadList } from '@/components/UploadList';
import { VoiceRecorder, type RecordingKind } from '@/components/VoiceRecorder';
import type { Hex } from '@/store/ids';
import type { MemberRecord, StoredEntity, TopicRecord, UploadRecord } from '@/store/types';
import styles from '@/components/Composer.module.css';
import panels from '@/components/panels.module.css';

/** A mention the user inserted, kept with its label so it can be re-verified. */
export interface PendingMention extends StoredEntity {
  label: string;
}

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string, entities: PendingMention[]) => void;
  /** Name of the message being replied to, or null when there is no reply. */
  replyToName: string | null;
  onCancelReply: () => void;
  /** True while editing an existing message rather than composing a new one. */
  editing: boolean;
  onCancelEdit: () => void;
  disabled: boolean;
  /** Shown when local persistence failed; never accompanied by a queued icon. */
  localFailure: string | null;

  // --- Phase 2 -------------------------------------------------------------
  uploads: readonly UploadRecord[];
  onFilesChosen: (files: File[]) => void;
  onRecorded: (blob: Blob, durationMs: number, mimeType: string, kind: RecordingKind) => void;
  onDiscardRecording: () => void;
  onPauseUpload: (uploadId: Hex) => void;
  onResumeUpload: (uploadId: Hex) => void;
  onCancelUpload: (uploadId: Hex) => void;
  onCreatePoll: () => void;
  /** Notice explaining why sending is currently refused, or null. */
  blockedReason: string | null;
  /** Draft conflict actions, present only while a conflict is unresolved. */
  conflictText: string | null;
  onKeepLocalDraft: () => void;
  onKeepRemoteDraft: () => void;

  // --- Phase 3 and 4 -------------------------------------------------------
  members: readonly MemberRecord[];
  topics: readonly TopicRecord[];
  topicId: Hex | null;
  onTopicChange: (topicId: Hex | null) => void;
  /** Channels: an administrator may publish without notifying subscribers. */
  canPublishSilently: boolean;
  silent: boolean;
  onSilentChange: (silent: boolean) => void;
  /** Polls belong to groups and channels, not to direct conversations. */
  canCreatePoll: boolean;
}

/**
 * The composer.
 *
 * Section 14 fixes its behaviour precisely and each rule here matches one
 * clause:
 *
 *   - Enter sends on desktop; Shift+Enter inserts a newline.
 *   - A message is never sent while an IME composition is active. This is the
 *     rule that matters most and is most often missed: in Japanese, Chinese and
 *     Korean input the Enter key commits the candidate the user is choosing.
 *     Sending on that keystroke would transmit half-typed text and swallow the
 *     keystroke that was supposed to accept a candidate. `isComposing` on the
 *     event is checked as well as the composition handlers, because the two
 *     disagree at the boundary in some browsers and the safe answer is "if
 *     either says composing, do not send".
 *   - An empty text-only message is invalid after trimming Unicode whitespace,
 *     while whitespace inside nonempty content is preserved — so the trim is
 *     used for the validity test only, never for the value that is sent.
 *   - The attachment menu from Phase 2 exposes photo/video, file, record voice
 *     and record video, and a message with a ready attachment is sendable with
 *     no text at all.
 *
 * Mentions complete inline while focus stays in the textarea. Moving focus into
 * a completion list would interrupt typing; the list is driven from the keyboard
 * by document-level handlers in `MentionPicker` instead.
 */
export function Composer(props: ComposerProps) {
  const { t } = useStrings();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [composing, setComposing] = useState(false);
  const [recording, setRecording] = useState<RecordingKind | null>(null);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [mentions, setMentions] = useState<PendingMention[]>([]);

  // Auto-grow, bounded. A composer that grows without limit eventually hides
  // the conversation it belongs to.
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }
    element.style.height = 'auto';
    element.style.height = `${String(Math.min(element.scrollHeight, 200))}px`;
  }, [props.value]);

  // A ready attachment is enough on its own: Section 06 allows a photo, a voice
  // note or a file with no caption.
  const hasReadyAttachment = props.uploads.some((upload) => upload.state === 'READY');
  const hasUnfinishedAttachment = props.uploads.some(
    (upload) => upload.state !== 'READY' && upload.state !== 'CANCELLED',
  );
  const canSubmit =
    !props.disabled &&
    props.blockedReason === null &&
    (props.value.trim().length > 0 || hasReadyAttachment) &&
    !hasUnfinishedAttachment;

  const submit = () => {
    // `\s` in a JavaScript regular expression already covers Unicode whitespace
    // including the ideographic space and the byte-order mark, so `trim` is the
    // Unicode-aware test Section 14 asks for.
    if (!canSubmit) {
      return;
    }
    props.onSend(props.value, mentions);
    setMentions([]);
    setMention(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // While the mention list is open its own handler owns Enter, Tab, the arrow
    // keys and Escape.
    if (mention !== null && ['Enter', 'Tab', 'ArrowUp', 'ArrowDown', 'Escape'].includes(event.key)) {
      return;
    }
    if (event.key !== 'Enter') {
      return;
    }
    if (event.shiftKey) {
      return;
    }
    if (composing || event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    submit();
  };

  const onInput = (value: string, caret: number) => {
    props.onChange(value);
    // Mentions only make sense where there is a member list to resolve against,
    // which Section 06 requires: "Mentions must resolve to visible members."
    setMention(props.members.length > 0 ? mentionQueryAt(value, caret) : null);
  };

  const chooseMention = (member: MemberRecord) => {
    const element = textareaRef.current;
    const caret = element?.selectionStart ?? props.value.length;
    if (!mention) {
      return;
    }
    const applied = applyMention(
      props.value,
      mention.start,
      caret,
      member.userId,
      member.displayName || member.userId.slice(0, 8),
    );
    props.onChange(applied.text);
    setMentions([
      ...mentions,
      { ...applied.entity, label: `@${member.displayName || member.userId.slice(0, 8)}` },
    ]);
    setMention(null);
    // The caret is restored after React has written the new value, or the
    // browser puts it at the end of the text.
    requestAnimationFrame(() => {
      element?.setSelectionRange(applied.caret, applied.caret);
      element?.focus();
    });
  };

  return (
    <div className={styles.composer}>
      {props.localFailure ? (
        <p className={styles.localFailure} role="alert">
          {props.localFailure}
        </p>
      ) : null}

      {props.conflictText !== null ? (
        <div className={styles.context} role="alert">
          <span className={styles.contextText} dir="auto">
            {t('draft.conflictTitle')} — {t('draft.otherVersion', { text: props.conflictText })}
          </span>
          <button type="button" className={styles.contextAction} onClick={props.onKeepLocalDraft}>
            {t('draft.keepLocal')}
          </button>
          <button type="button" className={styles.contextAction} onClick={props.onKeepRemoteDraft}>
            {t('draft.keepRemote')}
          </button>
        </div>
      ) : null}

      {props.replyToName !== null ? (
        <div className={styles.context}>
          <span className={styles.contextText} dir="auto">
            {t('composer.replyingTo', { name: props.replyToName })}
          </span>
          <button type="button" className={styles.contextAction} onClick={props.onCancelReply}>
            {t('composer.cancelReply')}
          </button>
        </div>
      ) : null}

      {props.editing ? (
        <div className={styles.context}>
          <span className={styles.contextText}>{t('composer.editing')}</span>
          <button type="button" className={styles.contextAction} onClick={props.onCancelEdit}>
            {t('composer.cancelEdit')}
          </button>
        </div>
      ) : null}

      {props.topics.length > 0 ? (
        <div className={styles.context}>
          <label className={styles.contextText} htmlFor="composer-topic">
            {t('topic.select')}
          </label>
          <select
            id="composer-topic"
            className={panels.select}
            value={props.topicId ?? ''}
            onChange={(event) => {
              props.onTopicChange(event.target.value === '' ? null : (event.target.value as Hex));
            }}
          >
            <option value="">{t('topic.all')}</option>
            {props.topics.map((topic) => (
              <option key={topic.topicId} value={topic.topicId} disabled={topic.closed}>
                {topic.title}
                {topic.closed ? ` (${t('topic.closed')})` : ''}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <UploadList
        uploads={props.uploads}
        onPause={props.onPauseUpload}
        onResume={props.onResumeUpload}
        onCancel={props.onCancelUpload}
      />

      {recording !== null ? (
        <VoiceRecorder
          kind={recording}
          onRecorded={(blob, durationMs, mimeType) => {
            props.onRecorded(blob, durationMs, mimeType, recording);
          }}
          onAccept={() => {
            setRecording(null);
          }}
          onDiscard={() => {
            props.onDiscardRecording();
            setRecording(null);
          }}
          onCancel={() => {
            setRecording(null);
          }}
        />
      ) : null}

      {props.blockedReason !== null ? (
        <p className={styles.hint} role="status">
          {props.blockedReason}
        </p>
      ) : null}

      {props.canPublishSilently ? (
        <label className={panels.checkboxRow}>
          <input
            type="checkbox"
            checked={props.silent}
            onChange={(event) => {
              props.onSilentChange(event.target.checked);
            }}
          />
          {t('channel.publishSilently')}
        </label>
      ) : null}

      <div className={styles.inputRow} style={{ position: 'relative' }}>
        <AttachmentPicker
          disabled={props.disabled || props.editing}
          onFiles={props.onFilesChosen}
          onRecordVoice={() => {
            setRecording('voice');
          }}
          onRecordVideo={() => {
            setRecording('video_note');
          }}
        />

        {props.canCreatePoll && !props.editing ? (
          <button
            type="button"
            className={panels.button}
            onClick={props.onCreatePoll}
            disabled={props.disabled}
          >
            {t('poll.create')}
          </button>
        ) : null}

        <label className={styles.visuallyHidden} htmlFor="composer-input">
          {t('composer.label')}
        </label>
        <textarea
          id="composer-input"
          ref={textareaRef}
          className={styles.input}
          value={props.value}
          rows={1}
          // The composer follows the text being typed, not the interface
          // language, so switching to an RTL language mid-draft is correct.
          dir="auto"
          placeholder={t('composer.placeholder')}
          aria-describedby="composer-hint"
          disabled={props.disabled}
          onChange={(event) => {
            onInput(event.target.value, event.target.selectionStart);
          }}
          onKeyDown={onKeyDown}
          onCompositionStart={() => {
            setComposing(true);
          }}
          onCompositionEnd={() => {
            setComposing(false);
          }}
        />

        {mention !== null ? (
          <MentionPicker
            query={mention.query}
            members={props.members}
            onChoose={chooseMention}
            onDismiss={() => {
              setMention(null);
            }}
          />
        ) : null}

        {/*
          A round control carries a glyph rather than a word: "Send" does not fit
          a 38 px circle at any readable size. The wording moves to aria-label, so
          a screen reader still hears Send, Publish or Save rather than the name
          of an arrow — and the three cases stay distinguishable, which they would
          not be if the label were dropped along with the text.
        */}
        <button
          type="button"
          className={styles.send}
          onClick={submit}
          disabled={!canSubmit}
          aria-label={
            props.editing
              ? t('composer.saveEdit')
              : props.canPublishSilently
                ? t('channel.publish')
                : t('composer.send')
          }
          title={
            props.editing
              ? t('composer.saveEdit')
              : props.canPublishSilently
                ? t('channel.publish')
                : t('composer.send')
          }
        >
          <span aria-hidden="true">{props.editing ? '✓' : '➤'}</span>
        </button>
      </div>

      <p id="composer-hint" className={styles.hint}>
        {t('composer.hint')}
        {props.members.length > 0 ? ` ${t('mention.hint')}` : ''}
        {hasUnfinishedAttachment ? ` ${t('upload.notReady')}` : ''}
      </p>
    </div>
  );
}
